/**
 * Sprint M5.40 (30.07.2026) — Block D2.
 * Sprint M5.42 (30.07.2026) — Block A2 Integration:
 * Muster-Sektion liest jetzt echte Daten von /api/pattern-recognition/me.
 *
 * `/dashboard/recommendations` — Empfehlungen-Stub (Pro-only).
 *
 * Schritt 5 der Value-Ladder: „Du erhältst Empfehlungen — zugeschnitten auf
 * dein Profil." Vollversion (Lernendes System mit Challenges, Mustern,
 * personalisierten Tipps) folgt in Sprint M5.42 / Q4 2026.
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Sparkles, Target, TrendingUp } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { isPremium } from "@/lib/licensing";
import { api } from "@/lib/api";

export default function DashboardRecommendations() {
  return (
    <DashboardLayout>
      {({ data }) => {
        const user = data?.user;
        // Free-User -> /pricing weiterleiten (Pro-only Feature).
        if (!isPremium(user)) {
          return <Navigate to="/pricing" replace />;
        }
        return (
          <div
            className="max-w-3xl mx-auto px-4 sm:px-6 py-10"
            data-testid="dashboard-recommendations-page"
          >
            <header className="mb-8">
              <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
                Schritt 5 · Lernendes System
              </p>
              <h1
                className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900"
                data-testid="dashboard-recommendations-headline"
              >
                Deine Empfehlungen
              </h1>
              <p
                className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl"
                data-testid="dashboard-recommendations-bigpicture"
              >
                Mit jedem Flexpfad lernt das System dich besser kennen. Du bekommst Empfehlungen und
                Challenges, die genau zu deinem Profil passen — und siehst, wo du gerade auf deinem
                Weg stehst.
              </p>
            </header>

            <section
              data-testid="recommendations-stub"
              className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-100 text-[#4563BB] flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-slate-900">
                    Empfehlungen — basierend auf deinem Profil
                  </h3>
                  <p
                    className="mt-2 text-sm text-slate-600 leading-relaxed"
                    data-testid="recommendations-stub-placeholder"
                  >
                    Das lernende System kommt in Sprint M5.42 (Q4 2026). Bis dahin sammle weiter
                    Flexpfade — sie bilden die Grundlage für personalisierte Empfehlungen.
                  </p>
                </div>
              </div>
            </section>

            <section
              data-testid="challenges-stub"
              className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                  <Target className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-slate-900">
                    Challenges zum Ausprobieren
                  </h3>
                  <p
                    className="mt-2 text-sm text-slate-600 leading-relaxed"
                    data-testid="challenges-stub-placeholder"
                  >
                    Kleine Aufgabenstellungen, die zu deinem Profil passen, folgen in Sprint M5.42.
                    Sie helfen dir, das Gelernte im Alltag zu verankern.
                  </p>
                </div>
              </div>
            </section>

            <section
              data-testid="patterns-stub"
              className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
            >
              <PatternRecognitionBlock />
            </section>
          </div>
        );
      }}
    </DashboardLayout>
  );
}


function PatternRecognitionBlock() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pattern-recognition/me").then(
      (r) => {
        setData(r.data);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, []);

  if (loading) {
    return (
      <div className="text-sm text-slate-500" data-testid="patterns-loading">
        Lade Muster…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
        <TrendingUp className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-extrabold text-slate-900">
          Deine Muster im Zeitverlauf
        </h3>
        {!data.has_patterns ? (
          <p
            className="mt-2 text-sm text-slate-600 leading-relaxed"
            data-testid="patterns-below-threshold"
          >
            {data.reason} — du hast aktuell{" "}
            <strong>{data.count} Flexpfade</strong>. Ab{" "}
            <strong>{data.min_required} Flexpfaden</strong> beginnen wir mit der
            Muster-Analyse. Sammle weiter!
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-600">
              Wir haben <strong>{data.count} Flexpfade</strong> analysiert und{" "}
              <strong>{data.patterns.length} Muster</strong> erkannt:
            </p>
            <ul
              className="mt-3 space-y-2"
              data-testid="patterns-list"
            >
              {data.patterns.map((p, i) => (
                <li
                  key={i}
                  data-testid={`patterns-item-${p.type}`}
                  className="flex items-start gap-2 text-sm text-slate-700"
                >
                  <span className="w-6 h-6 flex-shrink-0 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span>
                    <strong className="text-slate-900">
                      {p.type === "tageszeit" ? "Tageszeit-Muster" : "Fokustyp-Muster"}:
                    </strong>{" "}
                    In {p.percentage}% deiner Flexpfade zeigt sich{" "}
                    <em>{p.value}</em>.
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-slate-500 italic" data-testid="patterns-recommendation">
              {data.recommendation}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
